(() => {
  const moneyFormat = (cents) => {
    const currency = window.Shopify?.currency?.active || window.Shopify?.currency?.shopCurrency || 'USD';

    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const getCleanPath = (url) => String(url || window.location.pathname).split('?')[0].split('#')[0];

  class FalconBulkProduct extends HTMLElement {
    connectedCallback() {
      if (this.lifecycleController) return;

      this.sectionId = this.dataset.sectionId;
      this.baseProductUrl = this.dataset.productUrl || window.location.pathname;
      this.abortController = null;
      this.lifecycleController = new AbortController();
      this.isAdding = false;

      this.refreshRefs();
      const { signal } = this.lifecycleController;
      this.addEventListener('change', this.onOptionChange, { signal });
      this.addEventListener('input', this.onQuantityInput, { signal });
      this.addEventListener('click', this.onClick, { signal });
      this.bindForm();
      this.updateTotals();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.lifecycleController?.abort();
      this.lifecycleController = null;
    }

    refreshRefs() {
      this.mediaColumn = this.querySelector('.falcon-media-column');
      this.variantPicker = this.querySelector('.falcon-variant-picker');
      this.slider = this.querySelector(`#FalconSlider-${this.sectionId}`);
      this.thumbnails = this.querySelector(`#FalconThumbnails-${this.sectionId}`);
      this.sizeContainer = this.querySelector(`#FalconBulkSizes-${this.sectionId}`);
      this.totalMoney = this.querySelector(`#FalconTotalMoney-${this.sectionId}`);
      this.totalCount = this.querySelector(`#FalconTotalCount-${this.sectionId}`);
      this.submitButton = this.querySelector(`#FalconSubmitBtn-${this.sectionId}`);
      this.form = this.querySelector(`#FalconProductForm-${this.sectionId}`);
      this.notification = this.querySelector(`#FalconNotif-${this.sectionId}`);
    }

    onOptionChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.closest('.falcon-color-swatches')) return;

      this.fetchAndRenderSelection(input);
    };

    onQuantityInput = (event) => {
      if (event.target instanceof HTMLInputElement && event.target.classList.contains('falcon-bulk-input')) {
        this.updateTotals();
      }
    };

    onClick = (event) => {
      const previousButton = event.target.closest('.falcon-arrow-prev');
      const nextButton = event.target.closest('.falcon-arrow-next');
      const thumbnail = event.target.closest('.falcon-thumbnail');
      const accordionHeader = event.target.closest('.falcon-full-accordion-header');
      const submitButton = event.target.closest('.falcon-add-to-cart');

      if (submitButton) {
        event.preventDefault();
        this.submitSelectedItems();
      } else if (previousButton) {
        this.scrollSlider(-1);
      } else if (nextButton) {
        this.scrollSlider(1);
      } else if (thumbnail) {
        this.goToSlide(Number(thumbnail.dataset.index || 0));
      } else if (accordionHeader) {
        this.toggleFullAccordion(accordionHeader);
      }
    };

    buildSectionUrl(input) {
      const productPath = getCleanPath(input.dataset.productUrl || this.baseProductUrl);
      const url = new URL(productPath, window.location.origin);
      const optionValueId = input.dataset.optionValueId;

      url.searchParams.set('section_id', this.sectionId);

      if (optionValueId) {
        url.searchParams.set('option_values', optionValueId);
      }

      return url;
    }

    fetchAndRenderSelection(input) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.setLoading(true);

      fetch(this.buildSectionUrl(input).toString(), { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Variant section could not be loaded.');
          return response.text();
        })
        .then((html) => {
          this.renderFetchedSection(html);
          this.goToSelectedMedia(input);
          this.updateTotals();
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            this.setNotification('Variant information could not be loaded. Please try again.', 'error');
          }
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    renderFetchedSection(html) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const fetchedRoot =
        parsedDocument.querySelector(`falcon-bulk-product[data-section-id="${this.sectionId}"]`) ||
        parsedDocument;
      const fetchedMediaColumn = fetchedRoot.querySelector('.falcon-media-column');
      const fetchedVariantPicker = fetchedRoot.querySelector('.falcon-variant-picker');

      if (this.mediaColumn && fetchedMediaColumn) {
        this.mediaColumn.replaceWith(fetchedMediaColumn);
      }

      if (this.variantPicker && fetchedVariantPicker) {
        this.variantPicker.replaceWith(fetchedVariantPicker);
      }

      this.refreshRefs();
    }

    goToSelectedMedia(input) {
      const mediaId = input.dataset.variantMediaId;
      const imageSrc = input.dataset.variantImageSrc;
      const mediaPosition = Number.parseInt(input.dataset.variantMediaPosition, 10);

      if (mediaId && this.goToMedia(mediaId)) return;
      if (imageSrc && this.goToMediaSrc(imageSrc)) return;
      if (mediaPosition) this.goToSlide(mediaPosition - 1);
    }

    setLoading(isLoading) {
      if (!this.sizeContainer) return;
      this.sizeContainer.toggleAttribute('aria-busy', isLoading);
    }

    scrollSlider(direction) {
      if (!this.slider) return;
      const slides = Array.from(this.slider.querySelectorAll('.falcon-slide'));
      const currentIndex = slides.reduce((closestIndex, slide, index) => {
        const currentDistance = Math.abs(slides[closestIndex].offsetLeft - this.slider.scrollLeft);
        const nextDistance = Math.abs(slide.offsetLeft - this.slider.scrollLeft);
        return nextDistance < currentDistance ? index : closestIndex;
      }, 0);
      this.goToSlide(Math.max(0, Math.min(slides.length - 1, currentIndex + direction)));
    }

    goToSlide(index) {
      const slides = this.slider?.querySelectorAll('.falcon-slide');
      if (!slides?.[index]) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slider.scrollTo({ left: slides[index].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.updateActiveThumbnail(index);
    }

    goToMedia(mediaId) {
      if (!mediaId || !this.slider) return false;
      const slides = Array.from(this.slider.querySelectorAll('.falcon-slide'));
      const index = slides.findIndex((slide) => slide.dataset.mediaId == mediaId);
      if (index < 0) return false;
      this.goToSlide(index);
      return true;
    }

    goToMediaSrc(imageSrc) {
      if (!imageSrc || !this.slider) return false;
      const normalizedSrc = String(imageSrc).split('?')[0];
      const slides = Array.from(this.slider.querySelectorAll('.falcon-slide'));
      const index = slides.findIndex((slide) => (slide.dataset.mediaSrc || '').split('?')[0] === normalizedSrc);
      if (index < 0) return false;
      this.goToSlide(index);
      return true;
    }

    updateActiveThumbnail(index) {
      this.thumbnails?.querySelectorAll('.falcon-thumbnail').forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === index;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', String(isActive));
      });
    }

    toggleFullAccordion(header) {
      const accordion = header.parentElement;
      const icon = header.querySelector('.falcon-full-accordion-icon');

      this.querySelectorAll('.falcon-full-accordion.is-active').forEach((activeAccordion) => {
        if (activeAccordion === accordion) return;
        activeAccordion.classList.remove('is-active');
        const activeIcon = activeAccordion.querySelector('.falcon-full-accordion-icon');
        if (activeIcon) activeIcon.textContent = '+';
      });

      accordion.classList.toggle('is-active');
      if (icon) icon.textContent = accordion.classList.contains('is-active') ? '-' : '+';
    }

    updateTotals() {
      if (!this.sizeContainer) return;

      let totalMoney = 0;
      let totalCount = 0;

      this.sizeContainer.querySelectorAll('.falcon-bulk-size-item').forEach((item) => {
        const input = item.querySelector('.falcon-bulk-input');
        const quantity = Number.parseInt(input?.value, 10) || 0;
        const price = Number(item.dataset.price) || 0;
        const box = item.querySelector('.falcon-bulk-box');

        if (quantity > 0) {
          totalCount += quantity;
          totalMoney += quantity * price;
          box?.classList.add('has-qty');
        } else {
          box?.classList.remove('has-qty');
        }
      });

      if (this.totalMoney) this.totalMoney.textContent = moneyFormat(totalMoney);
      if (this.totalCount) this.totalCount.textContent = totalCount;
      if (this.submitButton) this.submitButton.disabled = totalCount === 0;
    }

    bindForm() {
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitSelectedItems();
      }, { signal: this.lifecycleController.signal });
    }

    submitSelectedItems() {
      this.refreshRefs();

      if (this.isAdding || this.submitButton?.disabled || !this.sizeContainer) return;

      const items = Array.from(this.sizeContainer.querySelectorAll('.falcon-bulk-size-item'))
        .map((item) => ({
          id: Number.parseInt(item.dataset.variantId, 10),
          quantity: Number.parseInt(item.querySelector('.falcon-bulk-input')?.value, 10) || 0,
        }))
        .filter((item) => item.id && item.quantity > 0);

      if (!items.length) return;

      this.addItemsToCart(items);
    }

    addItemsToCart(items) {
      const buttonText = this.submitButton?.querySelector('.falcon-btn-text');
      const defaultText = this.submitButton?.dataset.defaultText || 'Add to Cart';

      this.isAdding = true;
      this.setNotification('', '');
      if (this.submitButton) this.submitButton.disabled = true;
      if (buttonText) buttonText.textContent = 'Adding...';

      fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status && data.status >= 400) {
            throw new Error(data.description || 'Could not add to cart. Please try again.');
          }

          if (buttonText) buttonText.textContent = 'Added!';
          this.setNotification('Products added to cart!', 'success');
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          setTimeout(() => {
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          }, 800);
        })
        .catch((error) => {
          this.isAdding = false;
          if (this.submitButton) this.submitButton.disabled = false;
          if (buttonText) buttonText.textContent = defaultText;
          this.setNotification(error.message || 'An error occurred. Please try again.', 'error');
        });
    }

    setNotification(message, type) {
      if (!this.notification) return;

      this.notification.textContent = message;
      this.notification.className = type ? `falcon-notif falcon-notif-${type}` : 'falcon-notif';
      this.notification.style.display = message ? 'block' : 'none';
    }
  }

  if (!customElements.get('falcon-bulk-product')) {
    customElements.define('falcon-bulk-product', FalconBulkProduct);
  }
})();
